import { useState } from "react";
import { Timer } from "lucide-react";

const LEVELS = {
  beginner: [4.5, 4.5, 5.5, 6.0, 4.75, 2.5, 5.5, 8.0],
  intermediate: [4.0, 3.5, 4.5, 5.0, 4.25, 2.0, 4.5, 6.0],
  advanced: [3.5, 2.5, 3.5, 4.0, 3.75, 1.75, 3.5, 4.5],
};
const NAMES = ["SkiErg", "Sled Push", "Sled Pull", "Burpee Broad Jump", "Row", "Farmers Carry", "Sandbag Lunges", "Wall Balls"];

const fmtPace = (p) => `${Math.floor(p)}:${String(Math.round((p % 1) * 60)).padStart(2, "0")}`;
const fmtTotal = (min) => {
  const t = Math.round(min * 60);
  return `${Math.floor(t / 3600)}:${String(Math.floor((t % 3600) / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export const PaceCalculator = () => {
  const [level, setLevel] = useState("intermediate");
  const [pace, setPace] = useState(5.5);
  const [rox, setRox] = useState(45);
  const [splits, setSplits] = useState(LEVELS.intermediate);

  const pick = (l) => {
    setLevel(l);
    setSplits(LEVELS[l]);
  };
  const setSplit = (i, v) => setSplits(splits.map((s, j) => (j === i ? v : s)));

  const stationMin = splits.reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const runMin = 8 * pace;
  const roxMin = (8 * rox) / 60;
  const total = runMin + stationMin + roxMin;

  return (
    <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="pace-calculator">
      <div className="bg-[#0c0c0c] p-8">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Athlete Level</p>
        <div className="flex gap-2 mb-8">
          {Object.keys(LEVELS).map((l) => (
            <button
              key={l}
              onClick={() => pick(l)}
              data-testid={`pace-level-${l}`}
              className={`font-head uppercase text-sm font-bold px-4 py-2 border transition-colors ${
                level === l ? "bg-volt text-black border-volt" : "border-white/20 text-zinc-300 hover:border-white/50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          Run pace — <span className="text-volt">{fmtPace(pace)} /km</span>
        </p>
        <input
          type="range"
          min="4"
          max="9"
          step="0.25"
          value={pace}
          onChange={(e) => setPace(parseFloat(e.target.value))}
          className="w-full accent-[#FFED00] mb-8"
          data-testid="pace-run-slider"
        />

        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          Roxzone / transitions — <span className="text-volt">{rox}s each</span>
        </p>
        <input
          type="range"
          min="20"
          max="120"
          step="5"
          value={rox}
          onChange={(e) => setRox(parseInt(e.target.value, 10))}
          className="w-full accent-[#FFED00] mb-10"
          data-testid="pace-rox-slider"
        />

        <div className="border border-volt/40 bg-volt/5 p-6" data-testid="pace-total-box">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2 flex items-center gap-2">
            <Timer size={13} className="text-volt" /> Estimated finish time
          </p>
          <p className="font-head font-black text-5xl sm:text-6xl text-volt leading-none tabular-nums" data-testid="pace-total">
            {fmtTotal(total)}
          </p>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-3">
            Runs {fmtTotal(runMin)} · Stations {fmtTotal(stationMin)} · Roxzone {fmtTotal(roxMin)}
          </p>
        </div>
      </div>

      <div className="bg-[#0c0c0c] p-8">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Station splits (minutes)</p>
        <div className="grid grid-cols-2 gap-3">
          {NAMES.map((n, i) => (
            <label key={n} className="block">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-1">
                {String(i + 1).padStart(2, "0")} {n}
              </span>
              <input
                type="number"
                min="0"
                step="0.25"
                value={splits[i]}
                onChange={(e) => setSplit(i, e.target.value)}
                className="volt-input !py-2"
                data-testid={`pace-split-${i}`}
              />
            </label>
          ))}
        </div>
        <p className="text-zinc-500 text-xs mt-5 leading-relaxed">
          Presets are typical splits per level — tweak any station to match your
          numbers. Estimates only; race-day traffic and judging vary.
        </p>
      </div>
    </div>
  );
};
